import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box } from '@reservamos/elements';
import {
  trackFlexibleToggleOn,
  trackFlexibleToggleOff,
  trackFlexibleModalConfirmed,
  trackFlexibleModalCancelled,
} from 'metrics/user-analytics/loyalty';
import LabelSwitch from '../atoms/LabelSwitch';
import GuestCheckoutConfirmationModal from '../atoms/GuestCheckoutConfirmationModal';

/**
 * DotersGuestPurchaseSwitch component.
 * Renders a switch with confirmation modal for Doters guest purchase mode.
 * Encapsulates modal state to prevent parent re-renders.
 * @param {Object} props - Component props
 * @param {Function} props.onConfirm - Callback when user confirms/cancels (receives boolean)
 * @param {boolean} props.initialChecked - Initial checked state
 */
const DotersGuestPurchaseSwitch = ({ onConfirm, initialChecked }) => {
  const [showModal, setShowModal] = useState(false);
  const [isChecked, setIsChecked] = useState(initialChecked);
  const { t } = useTranslation('passengers');

  /**
   * Handles the switch click event
   * If turning ON: Shows confirmation modal first
   * If turning OFF: Disables immediately without modal
   */
  const handleSwitchClick = () => {
    if (isChecked) {
      // Turning OFF: No modal needed, just disable
      setIsChecked(false);
      trackFlexibleToggleOff();
      onConfirm(false);
    } else {
      // Turning ON: Show confirmation modal
      setShowModal(true);
    }
  };

  /**
   * Handles modal confirmation
   * Enables the switch and notifies parent
   */
  const handleModalConfirm = () => {
    trackFlexibleModalConfirmed();
    trackFlexibleToggleOn();
    setIsChecked(true);
    setShowModal(false);
    onConfirm(true);
  };

  /**
   * Handles modal cancellation
   * Closes modal without changing switch state
   */
  const handleModalCancel = () => {
    trackFlexibleModalCancelled();
    setShowModal(false);
  };

  return (
    <>
      <Box border="all" borderRadius="M" paddingHorizontal="S" paddingVertical="S">
        <LabelSwitch
          label={t('guest_purchase_switch.label')}
          disclaimer={isChecked ? t('guest_purchase_switch.disclaimer') : undefined}
          id="doters-guest-purchase-switch"
          checked={isChecked}
          onChange={handleSwitchClick}
          justifyContent="space-between"
          color="success"
        />
      </Box>

      {showModal && (
        <GuestCheckoutConfirmationModal
          onConfirm={handleModalConfirm}
          onCancel={handleModalCancel}
        />
      )}
    </>
  );
};

DotersGuestPurchaseSwitch.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  initialChecked: PropTypes.bool,
};

DotersGuestPurchaseSwitch.defaultProps = {
  initialChecked: false,
};

export default DotersGuestPurchaseSwitch;
